/* eslint-disable import/no-cycle */
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Store, select } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { pluck, map, mergeMap, take } from 'rxjs/operators';

import * as fromRoot from 'app/core/store';
import * as fromAuthSelect from 'app/core/store/selectors/auth.selectors';
import { API } from 'app/shared/constants/app-service-constants';
import { PlatformService } from 'app/core/services/platform.service';
import * as fromAuthActions from 'app/core/store/actions/auth.actions';
import { AppVersionService } from './app-version.service';
import { PushService } from './push.service';

const observe = 'response';

export const TOKENS = {
  bearerToken: 'x-amzn-Remapped-Authorization',
  refreshToken: 'refreshToken',
};

@Injectable()
export class AuthService {
  constructor(
    private http: HttpClient,
    private store: Store<fromRoot.State>,
    private platformService: PlatformService,
    private pushService: PushService,
    private appVersionService: AppVersionService,
  ) {}

  getRefreshToken(): Observable<string> {
    const refreshToken = localStorage.getItem(TOKENS.refreshToken);
    if (refreshToken) {
      const headers = new HttpHeaders({
        refreshToken,
      });
      return this.http.get(`${API.GET_ACCESS_TOKEN}`, { headers, observe }).pipe(
        pluck('headers'),
        map((responseHeaders: any) => {
          const bearerToken = responseHeaders.get('authorization');
          if (bearerToken) {
            localStorage.setItem(TOKENS.bearerToken, bearerToken);
          }
          return bearerToken;
        }),
      );
    }
    return of('');
  }

  login(values: { email: string; password: string }): Observable<any> {
    const deviceName = this.platformService.getDeviceName();
    const deviceType = this.platformService.isCordova() ? 'Device' : 'Browser';
    const appVersion = this.appVersionService.getCurrentAppVersion();

    return new Observable(observer => {
      this.platformService.getDeviceIdAsync().then(deviceId => {
        const body: any = {
          deviceId,
          deviceName,
          deviceType,
          username: values.email,
          password: values.password,
          appVersion,
        };

        this.http.post(`${API.AUTH}`, body).pipe(
          map((response: any) => {
            const { bearerToken, refreshToken } = response;
            this.saveTokens(bearerToken, refreshToken);
            return { bearerToken, refreshToken };
          }),
        ).subscribe({
          next: val => { observer.next(val); observer.complete(); },
          error: err => observer.error(err),
        });
      }).catch(err => observer.error(err));
    });
  }

  restorePassword(emailAddress: string, confirmEmailAddress: string): Observable<any> {
    const postData = {
      emailAddress: emailAddress.trim(),
      confirmEmailAddress: confirmEmailAddress.trim(),
    };

    return this.http.post(`${API.FORGOT_PASSWORD}`, postData);
  }

  resetPassword(password: string, confirmPassword: string, resetCode: string): Observable<any> {
    return this.http.post(`${API.RESET_PASSWORD}`, {
      password,
      confirmPassword,
      resetCode,
    });
  }

  verifyUserToken(userVerificationHash: string): Observable<any> {
    return this.http.post(`${API.VERIFY_USER}`, { userVerificationHash });
  }

  logout(): Observable<any> {
    this.clearTokens();
    this.pushService.unregister();
    return this.http.delete(`${API.LOGOUT}`, { responseType: 'text' });
  }

  isLoggined(): Observable<boolean> {
    return this.store.pipe(
      select(fromAuthSelect.getAuthAccessToken),
      take(1),
      mergeMap((isLoggined) => (isLoggined ? of(!!isLoggined) : this.checkAccessToken())),
    );
  }

  get isSessionActive(): boolean {
    return !!localStorage.getItem(TOKENS.refreshToken);
  }

  checkAccessToken(): Observable<boolean> {
    const bearerToken = localStorage.getItem(TOKENS.bearerToken);
    if (bearerToken) {
      this.store.dispatch(fromAuthActions.SetAuthCredentials({ payload: { bearerToken } }));
    }

    return of(!!bearerToken);
  }

  private saveTokens(bearerToken: string, refreshToken: string) {
    if (bearerToken) {
      localStorage.setItem(TOKENS.bearerToken, bearerToken);
    }

    if (refreshToken) {
      localStorage.setItem(TOKENS.refreshToken, refreshToken);
    }
  }

  private clearTokens() {
    localStorage.setItem(TOKENS.refreshToken, '');
    localStorage.setItem(TOKENS.bearerToken, '');
  }

  appLocalLogout() {
    this.clearTokens();
    this.store.dispatch(
      fromAuthActions.SetAuthCredentials({
        payload: {
          refreshToken: null,
          bearerToken: null,
        },
      }),
    );
  }
}
